import React, { useState, useRef, useEffect } from "react"
import ReactDOM from 'react-dom'

/*
 * If you want to use fast-xml-parser instead of DOMParser, 
 * install it with: npm install fast-xml-parser --save
 * Then uncomment the line below:
 */
// import { XMLParser } from "fast-xml-parser";

// Debug logging
console.log('Script initialized');
console.log('React version:', React.version);
console.log('ReactDOM version:', ReactDOM.version);

const divsToUpdate = document.querySelectorAll(".tailwind-update-me-exam-provider")
console.log('Found elements to update:', divsToUpdate.length);

// Ensure React is loaded before proceeding
if (!React || !ReactDOM) {
  console.error('React or ReactDOM is not loaded properly');
  throw new Error('React initialization failed');
}

// Function to render based on React version
function renderReactComponent(Component, props, container) {
  // For testing which API is available
  console.log('Checking available React APIs:', {
    render: typeof ReactDOM.render === 'function',
    createRoot: typeof ReactDOM.createRoot === 'function',
    hydrate: typeof ReactDOM.hydrate === 'function',
    version: React.version
  });

  // Create a unique ID for this render target
  const uniqueId = `istqb-exam-providers-react-${Math.random().toString(36).substring(2, 9)}`;
  
  // Create a clean container with unique ID
  const renderContainer = document.createElement('div');
  renderContainer.className = 'exam-provider-container';
  renderContainer.id = uniqueId;
  container.innerHTML = '';
  container.appendChild(renderContainer);
  
  try {
    // Try React 18 API first
    if (typeof ReactDOM.createRoot === 'function') {
      console.log('Using React 18 createRoot API');
      const root = ReactDOM.createRoot(renderContainer);
      root.render(<Component {...props} containerId={uniqueId} />);
      return true;
    }
    
    // Fall back to React 17 API
    if (typeof ReactDOM.render === 'function') {
      console.log('Using React 17 render API');
      ReactDOM.render(<Component {...props} containerId={uniqueId} />, renderContainer);
      return true;
    }
    
    // Last resort - try direct client import
    console.log('Trying direct client import');
    const createRoot = require('react-dom/client').createRoot;
    const root = createRoot(renderContainer);
    root.render(<Component {...props} containerId={uniqueId} />);
    return true;
  } catch (error) {
    console.error('All React rendering methods failed:', error);
    
    // Emergency fallback - direct DOM manipulation
    renderContainer.innerHTML = `
      <div style="padding: 20px; border: 1px solid #eee; border-radius: 4px;">
        <h3>Exam Provider</h3>
        <p>Please visit the official website for exam provider information.</p>
        ${props.pluginUrl ? `<a href="${props.pluginUrl}" target="_blank">Visit Website</a>` : ''}
      </div>
    `;
    return false;
  }
}

divsToUpdate.forEach(async (div, index) => {
  try {
    console.log(`Processing div ${index + 1}/${divsToUpdate.length}`);
    div.style.width = '100%'
    div.style.maxWidth = '100%'
    
    const preElement = div.querySelector("pre");
    if (!preElement) {
      throw new Error('Pre element not found');
    }
    console.log('Pre element found');
    
    const preContent = preElement.innerText;
    console.log('Pre content length:', preContent.length);
    let data;
    
    try {
      // First try to parse normally
      data = JSON.parse(preContent);
      console.log('JSON parsed successfully:', data);
    } catch (e) {
      console.log('Initial JSON parse failed, attempting to sanitize');
      console.error('Parse error:', e);
      
      // Try to clean the JSON string before parsing
      const cleanedContent = preContent
        // Remove invalid escape characters
        .replace(/\\([^"\/bfnrtu])/g, '$1')
        // Fix any truncated escape sequences
        .replace(/\\$/g, '')
        // Remove any invalid UTF-8 sequences
        .replace(/[\u0000-\u0019]+/g, '')
        // Ensure proper escaping of forward slashes
        .replace(/\//g, '\\/');
        
      console.log('Cleaned content:', cleanedContent);
      
      try {
        data = JSON.parse(cleanedContent);
        console.log('Parsed cleaned JSON:', data);
      } catch (secondError) {
        console.error('Failed to parse JSON even after cleaning:', secondError);
        // Provide fallback data structure
        data = {
          xmlData: null,
          pluginUrl: div.dataset.pluginUrl || ''
        };
        console.log('Using fallback data:', data);
      }
    }
    
    // Ensure we have a valid data object
    if (!data || typeof data !== 'object') {
      console.log('Invalid data object, using fallback');
      data = {
        xmlData: null,
        pluginUrl: div.dataset.pluginUrl || ''
      };
    }
    
    data.xmlData = null; // We'll fetch from API by default
    console.log('Final data object:', data);
    
    // Try to render with our universal renderer function
    console.log('Attempting to render React component');
    const renderSuccess = renderReactComponent(OurComponent, data, div);
    
    if (renderSuccess) {
      div.classList.remove("tailwind-update-me-exam-provider");
      console.log('Component rendered successfully');
    } else {
      console.warn('Using fallback rendering');
    }
    
  } catch (error) {
    console.error('Error initializing component:', error);
    console.error('Error stack:', error.stack);
    // Add visual feedback for users
    div.innerHTML = `
      <div style="color: #666; padding: 20px; text-align: center;">
        Loading exam provider data...
        <br>
        <small>If this message persists, please refresh the page.</small>
        <br>
        <small style="color: #999;">${error.message}</small>
      </div>
    `;
  }
});

function parseXMLData(xmlString) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, "text/xml")
  
  const providerNodes = xmlDoc.getElementsByTagName("a:TrainingProviderWrapper")
  const providers = Array.from(providerNodes).map(provider => ({
    name: provider.getElementsByTagName("a:Name")[0]?.textContent || "",
    country: provider.getElementsByTagName("a:Country")[0]?.getElementsByTagName("a:LongName")[0]?.textContent || "",
    email: (provider.getElementsByTagName("a:Email")[0]?.textContent || "").split(/\s+or\s+/)[0],
    website: provider.getElementsByTagName("a:WebSite")[0]?.textContent || "",
    address: provider.getElementsByTagName("a:Address")[0]?.textContent || "",
    city: provider.getElementsByTagName("a:City")[0]?.textContent || "",
    phone: provider.getElementsByTagName("a:Phone")[0]?.textContent || "",
    referencePerson: provider.getElementsByTagName("a:ReferencePerson")[0]?.textContent || "",
    accreditatedBoard: {
      nameBoard: provider.getElementsByTagName("a:AccreditatedBoard")[0]?.getElementsByTagName("a:NameBoard")[0]?.textContent || ""
    },
    courses: Array.from(provider.getElementsByTagName("a:TrainingProviderCourseWrapper")).map(course => ({
      name: course.getElementsByTagName("a:CertificationType")[0]?.getElementsByTagName("a:Name")[0]?.textContent || "",
      id: course.getElementsByTagName("a:Id")[0]?.textContent || "",
      type: course.getElementsByTagName("a:MaterialType")[0]?.textContent || "",
      languages: Array.from(course.getElementsByTagName("a:LanguageWrapper")).map(lang => 
        lang.getElementsByTagName("a:NameLanguage")[0]?.textContent || ""
      )
    }))
  }))

  return {
    providers: providers,
    certifications: providers[0]?.courses || []
  }
}

function truncateText(text, wordLimit = 6, charLimit = 30) {
  // First check character length
  if (text.length > charLimit) {
    return text.substring(0, charLimit) + '...'
  }
  
  // Then check word count
  const words = text.split(' ')
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ') + '...'
  }
  
  return text
}

function cleanExamName(examName, examLevel) {
  if (!examName) return '';
  
  let cleanedName = examName;
  
  // Remove the exam level text if it appears in the name
  if (examLevel && examLevel.trim() !== '') {
    // Escape special regex characters in examLevel
    const escapedLevel = examLevel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const levelRegex = new RegExp(`\\b${escapedLevel}\\b\\s*`, 'gi');
    cleanedName = cleanedName.replace(levelRegex, '');
  }
  
  // Remove "Core", "Specialist", "Expert", "Foundation", and "Advanced" from the exam name
  // Using regex with word boundaries to avoid partial matches
  cleanedName = cleanedName
    .replace(/\b(Core|Specialist|Expert|Foundation|Advanced)\b\s*/gi, '')
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing whitespace
  
  return cleanedName;
}

function groupExamsByLevel(exams) {
  if (!exams || exams.length === 0) return {};
  
  // Initialize dynamic groups - we'll build this based on the actual data
  const groups = {};
  
  // Helper function to determine certification level from acronym (fallback)
  const getCertificationLevel = (acronym) => {
    if (!acronym) return 'Other';
    if (acronym.startsWith('CTFL')) return 'Foundation Level';
    if (acronym.startsWith('CTAL')) return 'Advanced Level';
    if (acronym.startsWith('CTEL')) return 'Expert Level';
    return 'Other';
  };
  
  exams.forEach(exam => {
    // Use the actual examLevel from XML, fallback to acronym-based detection if not available
    const level = exam.examLevel || getCertificationLevel(exam.shortAcronym);
    
    // Initialize level if it doesn't exist
    if (!groups[level]) {
      groups[level] = [];
    }
    
    groups[level].push(exam);
  });
  
  // Sort exams within each level using our custom acronym sorting
  Object.keys(groups).forEach(level => {
    groups[level].sort(sortExamsByAcronym);
  });
  
  // Create ordered result with preferred level ordering
  const orderedResult = {};
  const levelOrder = ['Foundation Level', 'Advanced Level', 'Expert Level', 'Technologies & Approaches'];
  
  // Add levels in preferred order, then any additional levels
  const allLevels = [...new Set([...levelOrder, ...Object.keys(groups), 'Other'])];
  
  allLevels.forEach(level => {
    if (groups[level] && groups[level].length > 0) {
      orderedResult[level] = groups[level];
    }
  });
  
  return orderedResult;
}

function groupExamsForDropdown(exams) {
  if (!exams || exams.length === 0) return {};
  
  // Initialize dynamic groups - we'll build this based on the actual data
  const groups = {};
  
  // Helper function to determine certification level from acronym (fallback)
  const getCertificationLevel = (acronym) => {
    if (!acronym) return 'Other';
    if (acronym.startsWith('CTFL')) return 'Foundation Level';
    if (acronym.startsWith('CTAL')) return 'Advanced Level';
    if (acronym.startsWith('CTEL')) return 'Expert Level';
    return 'Other';
  };
  
  // First pass: collect all unique types and levels
  exams.forEach(exam => {
    const type = exam.type || 'Core'; // Default to Core if no type
    // Use the actual examLevel from XML, fallback to acronym-based detection if not available
    const level = exam.examLevel || getCertificationLevel(exam.shortAcronym);
    
    // Initialize type if it doesn't exist
    if (!groups[type]) {
      groups[type] = {};
    }
    
    // Initialize level if it doesn't exist
    if (!groups[type][level]) {
      groups[type][level] = [];
    }
    
    // Add exam to the appropriate group
    groups[type][level].push(exam);
  });
  
  // Sort exams within each level subgroup using our custom acronym sorting
  Object.keys(groups).forEach(type => {
    Object.keys(groups[type]).forEach(level => {
      groups[type][level].sort(sortExamsByAcronym);
    });
  });
  
  // Create ordered result with preferred type and level ordering
  const orderedResult = {};
  
  // Define preferred order for types and levels
  const typeOrder = ['Core', 'Specialist', 'Expert'];
  const levelOrder = ['Foundation Level', 'Advanced Level', 'Expert Level', 'Technologies & Approaches'];
  
  // Add types in preferred order, then any additional types
  const allTypes = [...new Set([...typeOrder, ...Object.keys(groups)])];
  
  allTypes.forEach(type => {
    if (groups[type] && Object.keys(groups[type]).length > 0) {
      orderedResult[type] = {};
      
      // Get all levels for this type
      const typeLevels = Object.keys(groups[type]);
      
      // Add levels in preferred order, then any additional levels
      const allLevels = [...new Set([...levelOrder, ...typeLevels, 'Other'])];
      
      allLevels.forEach(level => {
        if (groups[type][level] && groups[type][level].length > 0) {
          orderedResult[type][level] = groups[type][level];
        }
      });
    }
  });
  
  return orderedResult;
}

const ClearButton = ({ onClick, show }) => {
  if (!show) return null;
  
  return (
    <button 
      onClick={onClick}
      className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 z-20"
      title="Clear selection"
      type="button"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M6 18L18 6M6 6l12 12" 
        />
      </svg>
    </button>
  );
};

const Loader = () => (
  <div className="flex justify-center items-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const DropdownLoader = () => (
  <div className="p-4 text-center text-gray-500">Loading...</div>
);

const isDevelopment = () => {
  return window.location.href.includes('http://devlocal.local/sample-page/');
};

function sanitizeXmlString(xmlString) {
    if (!xmlString) return null;
    
    // Remove any BOM and non-printable characters
    xmlString = xmlString.replace(/^\uFEFF/, '');
    xmlString = xmlString.replace(/[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFF]/g, '');
    
    // Ensure proper XML declaration
    if (!xmlString.match(/<\?xml[^>]+>\s*/)) {
        xmlString = '<?xml version="1.0" encoding="UTF-8"?>' + xmlString;
    }
    
    // Fix common encoding issues
    xmlString = xmlString.replace(/&(?!#?[a-zA-Z0-9]+;)/g, '&amp;');
    
    return xmlString;
}

async function fetchExamData(pluginUrl, bypassCache = false) {
    try {
        // If bypass cache is enabled, go directly to the direct endpoint
        if (bypassCache) {
            console.log('Cache bypass enabled, using direct endpoint');
            return await fetchExamDataDirect(pluginUrl);
        }
        
        // Use the cached REST API endpoint - strict 5-minute cache only
        const restEndpoint = '/wp-json/istqb/v1/exam-providers';
        console.log('Fetching from 5-minute cache REST API:', restEndpoint);
        
        const response = await fetch(restEndpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Cache-Control': 'no-cache' // Force check for fresh cache
            },
            credentials: 'same-origin'
        });
    
        if (!response.ok) {
            console.error('Cache REST API failed:', {
                status: response.status,
                statusText: response.statusText
            });
            throw new Error(`Cache API failed: ${response.status} ${response.statusText}. No fallback available.`);
        }

        // Check cache status headers
        const cacheStatus = response.headers.get('X-Cache-Status');
        const cacheAge = response.headers.get('X-Cache-Age');
        const dataSource = response.headers.get('X-Data-Source');
        
        console.log('Cache Status:', {
            status: cacheStatus,
            age: cacheAge,
            source: dataSource
        });

        // If cache is a MISS or data is not from cache, this means cache is expired or invalid
        if (cacheStatus === 'MISS' || dataSource !== 'CACHED') {
            console.warn('Cache miss or expired cache detected - data may be stale');
        }

        const data = await response.json();
        
        if (!data || !data.providers || data.providers.length === 0) {
            throw new Error('Cache returned empty or invalid data');
        }

        console.log('Successfully fetched from cache:', {
            providerCount: data.providers.length,
            cacheStatus,
            dataSource
        });

        return data;
        
    } catch (error) {
        console.error('Failed to fetch from 5-minute cache:', error);
        // NO FALLBACKS - throw error to be handled by caller
        throw error;
    }
}

// Function to process JSON data directly (replaces XML parsing for JSON responses)
function processJsonData(jsonData) {
    try {
        console.log('Processing JSON data directly');
        
        if (!jsonData || !jsonData.providers) {
            console.error('Invalid JSON data structure');
            return null;
        }
        
        // The JSON data already has the correct structure with examLevel fields
        // We just need to ensure the exam grouping uses the examLevel correctly
        const providers = jsonData.providers.map(provider => {
            // Re-group exams to ensure proper structure and exam level usage
            const regroupedExams = {};
            
            // Process each exam type group
            Object.entries(provider.exams || {}).forEach(([type, exams]) => {
                if (!regroupedExams[type]) {
                    regroupedExams[type] = [];
                }
                
                // Ensure each exam has the examLevel field
                const processedExams = exams.map(exam => ({
                    ...exam,
                    examLevel: exam.examLevel || 'Other' // Ensure examLevel is present
                }));
                
                regroupedExams[type] = processedExams.sort(sortExamsByAcronym);
            });
            
            return {
                ...provider,
                exams: regroupedExams
            };
        });
        
        // Process unique exams with examLevel
        const uniqueExams = (jsonData.exams || []).map(exam => ({
            ...exam,
            examLevel: exam.examLevel || 'Other'
        })).sort(sortExamsByAcronym);
        
        return {
            providers: providers.sort((a, b) => (a.name || '').trim().localeCompare((b.name || '').trim())),
            countries: jsonData.countries || [],
            languages: jsonData.languages || [],
            exams: uniqueExams
        };
        
    } catch (error) {
        console.error('Error processing JSON data:', error);
        throw error;
    }
}

// Fallback function for direct API calls (original functionality)
async function fetchExamDataDirect(pluginUrl) {
    try {
        console.log('Attempting to fetch from direct endpoint:', `${pluginUrl}/direct-exam-providers.php`);
        
        // Ensure pluginUrl is properly formatted
        const baseUrl = pluginUrl.endsWith('/') ? pluginUrl.slice(0, -1) : pluginUrl;
        const endpoint = `${baseUrl}/direct-exam-providers.php`;
        
        console.log('Final direct endpoint URL:', endpoint);
        
        const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
                'Content-Type': 'application/json'
            },
            // Add credentials to ensure cookies are sent
            credentials: 'same-origin'
        });
    
    if (!response.ok) {
            console.error('Server response:', {
                status: response.status,
                statusText: response.statusText
            });
            
            // Try to get more error details if available
            try {
      const errorText = await response.text();
                console.error('Server error details:', errorText);
            } catch (textError) {
                console.error('Could not read error details');
            }
            
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Return the text response
        return await response.text();
  } catch (error) {
        console.error('Error fetching exam data from direct endpoint:', error);
        console.error('Stack trace:', error.stack);
    throw error;
  }
}

// Helper function to safely get text content from XML elements
function getElementText(parent, tagName) {
    const element = parent.getElementsByTagName(tagName)[0];
    return element ? element.textContent : '';
}

function decodeUnicodeEscapes(str) {
  return str.replace(/\\u([a-fA-F0-9]{4})/g, (match, grp) =>
    String.fromCharCode(parseInt(grp, 16))
  );
}

// Function to use fast-xml-parser if available
function parseFastXML(xmlString) {
  // Check if fast-xml-parser is imported
  if (typeof XMLParser === 'undefined') {
    console.log('fast-xml-parser not available, using DOMParser instead');
    return null;
  }
  
  try {
    // Clean the XML string of any potential issues
    xmlString = xmlString
      .trim()
      .replace(/^["']|["']$/g, '') // Remove quotes
      .replace(/\\"/g, '"') // Unescape quotes
      .replace(/\\\//g, '/') // Unescape slashes
      .replace(/^\uFEFF/, '') // Remove BOM
      .replace(/[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFF]/g, '') // Remove invalid XML characters
      .replace(/&(?!(?:apos|quot|[gl]t|amp);|#)/g, '&amp;'); // Fix unescaped ampersands

    // Ensure proper XML declaration
    if (!xmlString.startsWith('<?xml')) {
      xmlString = '<?xml version="1.0" encoding="UTF-8"?>' + xmlString;
    }

    // Extract the XML content if possible
    const xmlMatch = xmlString.match(/<\?xml.*?<\/s:Envelope>/s);
    if (xmlMatch) {
      xmlString = xmlMatch[0];
      console.log('Successfully extracted XML content');
    } else {
      console.warn('Could not find complete XML document, trying alternative extraction');
      const startPos = xmlString.indexOf('<?xml');
      const endPos = xmlString.lastIndexOf('</s:Envelope>');
      if (startPos !== -1 && endPos !== -1) {
        xmlString = xmlString.substring(startPos, endPos + 12);
        console.log('Extracted XML using alternative method');
      }
    }
    
    // Parse XML using fast-xml-parser
    const options = {
      ignoreAttributes: false,
      attributeNamePrefix: "@_",
      allowBooleanAttributes: true,
      parseAttributeValue: true,
      parseTagValue: true,
      trimValues: true,
      isArray: (name, jpath, isLeafNode, isAttribute) => {
        // Make sure these tags are always treated as arrays
        const arrayTags = [
          'BoardExamProviderWrapper',
          'BoardExamProviderExamWrapper',
          'Languages'
        ];
        return arrayTags.includes(name);
      }
    };
    
    const parser = new XMLParser(options);
    const result = parser.parse(xmlString);
    
    console.log('Successfully parsed XML with fast-xml-parser');
    return result;
  } catch (error) {
    console.error('Error parsing XML with fast-xml-parser:', error);
    return null;
  }
}

// Enhanced XML parsing function using DOMParser
function parseXMLWithDOMParser(xmlString) {
  try {
    // Log first 200 characters for debugging
    console.log('Raw XML input:', xmlString.substring(0, 200));
    
    // Check if the input is JSON encoded
    try {
      const decoded = JSON.parse(xmlString);
      if (typeof decoded === 'string') {
        console.log('Input was JSON encoded, decoded successfully');
        xmlString = decoded;
      }
    } catch (e) {
      // Not JSON encoded, continue with original string
      console.log('Input is not JSON encoded');
    }
    
    // Clean the XML string for better compatibility
    xmlString = xmlString
      .trim()
      .replace(/^["']|["']$/g, '') // Remove quotes
      .replace(/\\"/g, '"') // Unescape quotes
      .replace(/\\\//g, '/') // Unescape slashes
      .replace(/^\uFEFF/, '') // Remove BOM
      .replace(/[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFF]/g, '') // Remove invalid XML characters
      .replace(/&(?!(?:apos|quot|[gl]t|amp);|#)/g, '&amp;'); // Fix unescaped ampersands
    
    // Ensure proper XML declaration
    if (!xmlString.startsWith('<?xml')) {
      xmlString = '<?xml version="1.0" encoding="UTF-8"?>' + xmlString;
    }
    
    // Try to extract just the SOAP envelope if it exists
    const envelopeMatch = xmlString.match(/<(?:SOAP-ENV:|s:)?Envelope[\s\S]*?<\/(?:SOAP-ENV:|s:)?Envelope>/);
    if (envelopeMatch) {
      console.log('Found SOAP envelope, extracting');
      xmlString = '<?xml version="1.0" encoding="UTF-8"?>' + envelopeMatch[0];
    }
    
    console.log('Cleaned XML:', xmlString.substring(0, 200));
    
    // Parse XML using DOMParser
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      console.error('XML parsing error:', parserError.textContent);
      
      // Try alternative parsing approach
      console.log('Attempting alternative parsing approach');
      const alternativeXml = xmlString
        .replace(/<\?xml[^>]*\?>/, '') // Remove XML declaration
        .replace(/xmlns[^>]*>/g, '>') // Remove namespace declarations temporarily
        .trim();
      
      const wrappedXml = `<?xml version="1.0" encoding="UTF-8"?><root>${alternativeXml}</root>`;
      const altDoc = parser.parseFromString(wrappedXml, "text/xml");
      
      if (!altDoc.querySelector('parsererror')) {
        console.log('Alternative parsing successful');
        return altDoc;
      }
      
      throw new Error('XML parsing failed');
    }
    
    // Create a more permissive namespace-aware function to get elements
    const getElementByTagNameWithNamespace = (doc, tagName) => {
      // Try with different namespace prefixes
      const prefixes = ['SOAP-ENV:', 'ns1:', 'ns2:', 's:', 'a:', ''];
      
      for (const prefix of prefixes) {
        const elements = doc.getElementsByTagName(prefix + tagName);
        if (elements && elements.length > 0) {
          return elements;
        }
      }
      
      // If nothing found, try querySelector with all namespaces
      try {
        const elements = doc.querySelectorAll(`*|${tagName}`);
        if (elements && elements.length > 0) {
          return elements;
        }
      } catch (e) {
        console.warn('QuerySelector namespace wildcard failed:', e);
      }
      
      return [];
    };
    
    // Get the response element with multiple namespace approaches
    const getResponseElement = (doc) => {
      // Try multiple ways to find the response data
      const responseElement = 
        doc.querySelector('GetExamProviderCertificationsByBoardResult') || 
        doc.querySelector('ns2\\:GetExamProviderCertificationsByBoardResult') ||
        doc.querySelector('SOAP-ENV\\:Body')?.querySelector('*[GetExamProviderCertificationsByBoardResult]') ||
        doc.querySelector('*|GetExamProviderCertificationsByBoardResult');
      
      if (responseElement) return responseElement;
      
      // If direct query fails, try traversing the document structure
      const envelope = getElementByTagNameWithNamespace(doc, 'Envelope');
      if (envelope && envelope.length > 0) {
        const body = getElementByTagNameWithNamespace(envelope[0], 'Body');
        if (body && body.length > 0) {
          const response = getElementByTagNameWithNamespace(body[0], 'GetExamProviderCertificationsByBoardResponse');
          if (response && response.length > 0) {
            const result = getElementByTagNameWithNamespace(response[0], 'GetExamProviderCertificationsByBoardResult');
            if (result && result.length > 0) {
              return result[0];
            }
          }
        }
      }
      
      // If still not found, try looking for any element containing provider data
      const providerWrappers = doc.querySelectorAll('*[BoardExamProviderWrapper]');
      if (providerWrappers.length > 0) {
        const wrapper = document.createElement('div');
        wrapper.append(...providerWrappers);
        return wrapper;
      }
      
      return null;
    };
    
    // Get the response element
    const responseElement = getResponseElement(xmlDoc);
    
    if (!responseElement) {
      console.error('Could not find response element');
      console.log('Document structure:', xmlDoc.documentElement.outerHTML.substring(0, 500));
      throw new Error('Could not find response data in XML');
    }
    
    if (!responseElement.hasChildNodes()) {
      console.log('Empty response received');
      return {
        providers: [],
        countries: [],
        languages: [],
        exams: []
      };
    }
    
    // Helper function to get elements with namespace consideration
    const getElements = (parent, tagName) => {
      const prefixes = ['ns1:', 'ns2:', 'a:', ''];
      for (const prefix of prefixes) {
        const elements = parent.getElementsByTagName(prefix + tagName);
        if (elements && elements.length > 0) return elements;
      }
      return [];
    };
    
    // Get provider wrappers
    let providerNodes = getElements(responseElement, 'BoardExamProviderWrapper');
    if (!providerNodes || providerNodes.length === 0) {
      console.error('No provider nodes found');
      throw new Error('No provider data found');
    }
    
    // Extract provider data
    const providers = Array.from(providerNodes).map(provider => {
      const getTextContent = (elem, tagName) => {
        const element = getElements(elem, tagName)[0];
        return element ? (element.textContent || "") : "";
      };
      
      // Extract board information
      const boardWrappers = getElements(provider, 'BoardWrapper');
      const boards = Array.from(boardWrappers).map(board => ({
        codeBoard: getTextContent(board, 'CodeBoard'),
        idBoard: getTextContent(board, 'IdBoard'),
        nameBoard: getTextContent(board, 'NameBoard'),
        countriesOfOperation: getTextContent(board, 'CountriesOfOperation')
      })).filter(board => 
        board.codeBoard && board.codeBoard.trim() !== '' && 
        board.nameBoard && board.nameBoard.trim() !== ''
      );

      // Check if provider has multiple valid boards
      const hasMultipleBoards = boards.length > 1;
      
      // Get exam wrappers for this provider
      const examNodes = getElements(provider, 'BoardExamProviderExamWrapper');
      
      const examsWithLanguages = Array.from(examNodes)
        .map(exam => {
          const examName = getTextContent(exam, 'Exam');
          const examType = getTextContent(exam, 'StreamExamType') || "Other";
          const examLevel = getTextContent(exam, 'ExamLevel') || "Other"; // Extract ExamLevel
          const languages = getTextContent(exam, 'Languages')
            .split(',')
            .map(l => l.trim())
            .filter(l => l);
          
          return {
            name: examName,
            id: getTextContent(exam, 'Id'),
            languages: languages,
            shortAcronym: getTextContent(exam, 'ShortAcronymWithLevel'),
            type: examType,
            examLevel: examLevel, // Add examLevel to the object
            typeOfExams: getTextContent(exam, 'TypeOfExams')
              .split(',')
              .map(t => t.trim())
              .filter(t => t)
          };
        })
        .filter(exam => exam.languages.length > 0);

      // Group exams by type
      const groupedExams = examsWithLanguages.reduce((groups, exam) => {
        const type = exam.type || 'Other';
        if (!groups[type]) {
          groups[type] = [];
        }
        groups[type].push(exam);
        return groups;
      }, {});

      // Check for Preferred status
      const preferred = getTextContent(provider, 'Preferred') === 'true';

      // Return the complete provider object
      return {
        name: decodeUnicodeEscapes(getTextContent(provider, 'ExamProviderName')),
        country: decodeUnicodeEscapes(getTextContent(provider, 'Country')),
        email: decodeUnicodeEscapes(getTextContent(provider, 'Email')),
        website: decodeUnicodeEscapes(getTextContent(provider, 'WebSite')),
        address: decodeUnicodeEscapes(getTextContent(provider, 'Address')),
        city: decodeUnicodeEscapes(getTextContent(provider, 'City')),
        phone: decodeUnicodeEscapes(getTextContent(provider, 'Phone')),
        referencePerson: decodeUnicodeEscapes(getTextContent(provider, 'ReferencePerson')),
        examProviderBoardName: decodeUnicodeEscapes(getTextContent(provider, 'ExamProviderBorderName')),
        countriesOfOperation: decodeUnicodeEscapes(getTextContent(provider, 'CountriesOfOperation'))
          .split(',')
          .map(c => c.trim()),
        exams: groupedExams,
        preferred: preferred,
        hasMultipleBoards: hasMultipleBoards,
        boards: boards
      };
    });
    
    // Extract unique exams across all providers
    const uniqueExams = Array.from(
      new Map(
        providers.flatMap(p => 
          Object.entries(p.exams).flatMap(([type, exams]) => 
            exams.map(e => [e.name, { 
              name: e.name, 
              type: type, 
              shortAcronym: e.shortAcronym || '',
              examLevel: e.examLevel || 'Other'
            }])
          )
        )
      ).values()
    ).sort(sortExamsByAcronym);
    
    // Return all collected data
    return {
      providers: providers.sort((a, b) => (a.name || '').trim().localeCompare((b.name || '').trim())),
      countries: [...new Set(providers.flatMap(p => p.countriesOfOperation))].filter(c => c).sort(),
      languages: [...new Set(providers.flatMap(p => 
        Object.values(p.exams).flat().flatMap(e => e.languages)
      ))].filter(l => l).sort(),
      exams: uniqueExams
    };

  } catch (error) {
    console.error('Error in parseXMLWithDOMParser:', error);
    throw error;
  }
}

// Function to extract data using regular expressions as a last resort
function createFallbackXmlDoc(xmlString) {
  console.log('Creating fallback XML document using regex extraction');
  
  try {
    const parser = new DOMParser();
    const xml = parser.parseFromString('<root></root>', 'text/xml');
    
    // Create a result element to hold all providers
    const resultElement = xml.createElement("ns1:GetExamProviderCertificationsByBoardResult");
    xml.documentElement.appendChild(resultElement);
    
    // Extract provider blocks
    const providerRegex = /<(?:ns1:)?BoardExamProviderWrapper>[\s\S]*?<\/(?:ns1:)?BoardExamProviderWrapper>/g;
    const providerMatches = xmlString.match(providerRegex) || [];
    
    providerMatches.forEach(providerXml => {
      const provider = xml.createElement("ns1:BoardExamProviderWrapper");
      resultElement.appendChild(provider);
      
      // Extract basic provider data
      const extractField = (fieldName) => {
        const regex = new RegExp(`<(?:ns1:)?${fieldName}>(.*?)</(?:ns1:)?${fieldName}>`, 's');
        const match = providerXml.match(regex);
        if (match && match[1]) {
          const field = xml.createElement(`ns1:${fieldName}`);
          field.textContent = match[1].trim();
          provider.appendChild(field);
        }
      };
      
      // Extract essential fields
      ['ExamProviderName', 'Country', 'Email', 'WebSite', 'Address', 
       'City', 'Phone', 'ReferencePerson', 'ExamProviderBorderName', 
       'CountriesOfOperation', 'Preferred'].forEach(extractField);
      
      // Extract exam blocks
      const examRegex = /<(?:ns1:)?BoardExamProviderExamWrapper>[\s\S]*?<\/(?:ns1:)?BoardExamProviderExamWrapper>/g;
      const examMatches = providerXml.match(examRegex) || [];
      
      examMatches.forEach(examXml => {
        const exam = xml.createElement("ns1:BoardExamProviderExamWrapper");
        provider.appendChild(exam);
        
        // Extract exam fields
        ['Id', 'Exam', 'StreamExamType', 'ExamLevel', 'Languages', 
         'ShortAcronymWithLevel', 'TypeOfExams'].forEach(fieldName => {
          const regex = new RegExp(`<(?:ns1:)?${fieldName}>(.*?)</(?:ns1:)?${fieldName}>`, 's');
          const match = examXml.match(regex);
          if (match && match[1]) {
            const field = xml.createElement(`ns1:${fieldName}`);
            field.textContent = match[1].trim();
            exam.appendChild(field);
          }
        });
      });
    });
    
    console.log('Successfully created fallback XML document');
    return xml;
  } catch (error) {
    console.error('Error creating fallback XML:', error);
    throw new Error('Failed to create fallback XML');
  }
}

function parseExamProviderData(xmlString) {
  try {
    console.log('Raw XML string length:', xmlString?.length);
    
    if (!xmlString || typeof xmlString !== 'string') {
      console.error('Invalid XML string received');
      return null;
    }

    // If the XML string is very large, log just the start and end
    if (xmlString.length > 1000) {
      console.log('XML start:', xmlString.substring(0, 200));
      console.log('XML end:', xmlString.substring(xmlString.length - 200));
    }

    // Clean the XML string for better compatibility
    xmlString = xmlString
      .trim()
      .replace(/^["']|["']$/g, '') // Remove quotes
      .replace(/\\"/g, '"') // Unescape quotes
      .replace(/\\\//g, '/') // Unescape slashes
      .replace(/^\uFEFF/, '') // Remove BOM
      .replace(/<br\s*\/?>/gi, '') // Remove br tags
      .replace(/<\/?font[^>]*>/gi, '') // Remove font tags
      .replace(/<\/?table[^>]*>/gi, '') // Remove table tags
      .replace(/<\/?tr[^>]*>/gi, '') // Remove tr tags
      .replace(/<\/?td[^>]*>/gi, '') // Remove td tags
      .replace(/\)\s*\d+\s*\.\s*\d+\s*\.\s*\d+/gi, ''); // Remove version numbers

    // First try fast-xml-parser if available
    const fastResult = parseFastXML(xmlString);
    if (fastResult) {
      try {
        // Process fastResult
        // ... existing fast-xml-parser code ...
      } catch (error) {
        console.error('Error processing fast-xml-parser result:', error);
        // Fall back to DOMParser below
      }
    }

    // If fast-xml-parser failed or is not available, fallback to DOMParser
    try {
      // Parse the XML document
      return parseXMLWithDOMParser(xmlString);
    } catch (error) {
      console.error('Error parsing XML with DOMParser:', error);
      
      // Try fallback with regex extraction
      try {
        console.log('Trying regex-based fallback extraction');
        if (xmlString.includes('BoardExamProviderWrapper')) {
          const fallbackDoc = createFallbackXmlDoc(xmlString);
          
          if (fallbackDoc) {
            // Process fallback document similar to normal processing
            // This extracts provider data using direct DOM methods
            const providers = Array.from(fallbackDoc.getElementsByTagName('ns1:BoardExamProviderWrapper')).map(provider => {
              // Process provider similar to the DOM parser method
              // ... similar to existing code ...
              
              // For brevity, returning a simplified structure here
              return {
                name: provider.querySelector('ns1\\:ExamProviderName')?.textContent || "",
                country: provider.querySelector('ns1\\:Country')?.textContent || "",
                email: provider.querySelector('ns1\\:Email')?.textContent || "",
                website: provider.querySelector('ns1\\:WebSite')?.textContent || "",
                // ... other fields
                exams: {} // Process exams
              };
            });
            
        return {
              providers: providers,
              countries: [...new Set(providers.map(p => p.country))].filter(c => c).sort(),
          languages: [],
          exams: []
        };
      }
        }
      } catch (fallbackError) {
        console.error('Fallback extraction failed:', fallbackError);
      }
      
      throw error;
    }
  } catch (error) {
    console.error('Error in parseExamProviderData:', error);
    throw error;
  }
}

function useWindowSize() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1000); // Switch to mobile view under 768px
    }

    // Set initial size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

// Custom exam sorting function
function sortExamsByAcronym(examA, examB) {
  const acronymA = examA.shortAcronym || '';
  const acronymB = examB.shortAcronym || '';
  
  // Define the sorting order according to user requirements
  const suffixOrder = [
    'TA', 'TM', 'TTA', 'TAE', 'TM-OTM', 'TM-SM', 'TM-MTT', 'TM-STM', 'ITP-ATP', 'ITP-ITPI',
    'ATT', 'ATLaS', 'AT', 'AI', 'MAT', 'MBT', 'TAS', 'AcT', 'PT', 'SEC',
    'UT', 'AuT', 'GT', 'STE', 'GaMe'
  ];
  
  // Helper function to get sort priority
  const getSortPriority = (acronym) => {
    if (!acronym) return { priority: 9999, suffix: '', examGroup: 'other' };
    
    // Check if it starts with CTFL-, CTAL-, or CTEL-
    const isCTFL = acronym.startsWith('CTFL-') || acronym === 'CTFL';
    const isCTAL = acronym.startsWith('CTAL-') || acronym === 'CTAL';
    const isCTEL = acronym.startsWith('CTEL-') || acronym === 'CTEL';
    
    // Extract suffix after dash
    let suffix = '';
    if (acronym.includes('-')) {
      suffix = acronym.substring(acronym.indexOf('-') + 1);
    } else if (acronym === 'CTFL' || acronym === 'CTAL' || acronym === 'CTEL') {
      suffix = ''; // CTFL/CTAL/CTEL without dash should come first among their respective exams
    }
    
    // For CTFL exams, give them highest priority (lowest number)
    if (isCTFL) {
      if (suffix === '') {
        return { priority: 0, suffix: '', examGroup: 'CTFL' }; // CTFL without suffix comes first
      }
      const suffixIndex = suffixOrder.indexOf(suffix);
      return { 
        priority: suffixIndex === -1 ? 100 : suffixIndex + 1, // +1 to put CTFL first
        suffix: suffix,
        examGroup: 'CTFL' 
      };
    }
    
    // For CTAL exams, give them second priority (after CTFL)
    if (isCTAL) {
      if (suffix === '') {
        return { priority: 200, suffix: '', examGroup: 'CTAL' }; // CTAL without suffix comes first among CTAL
      }
      const suffixIndex = suffixOrder.indexOf(suffix);
      return { 
        priority: suffixIndex === -1 ? 300 : suffixIndex + 201, // +201 to put after CTFL but before others
        suffix: suffix,
        examGroup: 'CTAL' 
      };
    }
    
    // For CTEL exams, give them third priority (after CTFL and CTAL)
    if (isCTEL) {
      if (suffix === '') {
        return { priority: 400, suffix: '', examGroup: 'CTEL' }; // CTEL without suffix comes first among CTEL
      }
      const suffixIndex = suffixOrder.indexOf(suffix);
      return { 
        priority: suffixIndex === -1 ? 500 : suffixIndex + 401, // +401 to put after CTFL/CTAL but before others
        suffix: suffix,
        examGroup: 'CTEL' 
      };
    }
    
    // For non-CTFL/CTAL/CTEL exams, start after CTEL exams
    const suffixIndex = suffixOrder.indexOf(suffix);
    return { 
      priority: suffixIndex === -1 ? 9999 : suffixIndex + 1000, // +1000 to put after CTFL/CTAL/CTEL
      suffix: suffix,
      examGroup: 'other' 
    };
  };
  
  const priorityA = getSortPriority(acronymA);
  const priorityB = getSortPriority(acronymB);
  
  // First sort by priority
  if (priorityA.priority !== priorityB.priority) {
    return priorityA.priority - priorityB.priority;
  }
  
  // If same priority, sort alphabetically by name
  return examA.name.localeCompare(examB.name);
}

// Add helper function to check if we're on istqb.org production domain
const isProductionDomain = () => {
  return window.location.hostname === 'istqb.org' || window.location.hostname.endsWith('.istqb.org');
};

function OurComponent(props) {
  const [useApi, setUseApi] = useState(true);
  const [showCacheControls, setShowCacheControls] = useState(false);
  const [bypassCache, setBypassCache] = useState(false);
  const [searchTerm, setSearchTerm] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [countrySearch, setCountrySearch] = useState("")
  const [languageSearch, setLanguageSearch] = useState("")
  const [countryOpen, setCountryOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [examProviderSearch, setExamProviderSearch] = useState("")
  const [examSearch, setExamSearch] = useState("")
  const [examTypeSearch, setExamTypeSearch] = useState("")
  const [examProviderOpen, setExamProviderOpen] = useState(false)
  const [examOpen, setExamOpen] = useState(false)
  const [examTypeOpen, setExamTypeOpen] = useState(false)
  const [showSortLabels, setShowSortLabels] = useState(false)
  
  // Add modal state variables
  const [showModal, setShowModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const countryRef = useRef(null)
  const languageRef = useRef(null)
  const examProviderRef = useRef(null)
  const examRef = useRef(null)
  const examTypeRef = useRef(null)

  const [examProviders, setExamProviders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [exams, setExams] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  // Remove lastWorkingData - no fallbacks to old data allowed
  const [cacheExpiryTime, setCacheExpiryTime] = useState(null);

  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase())
  ).sort((a, b) => a.localeCompare(b));

  const filteredLanguages = languages.filter(language =>
    language.toLowerCase().includes(languageSearch.toLowerCase())
  ).sort((a, b) => a.localeCompare(b));

  const filteredExamProviders = examProviders.filter(provider =>
    provider.name?.toLowerCase().includes(examProviderSearch.toLowerCase())
  ).sort((a, b) => (a.name || '').trim().localeCompare((b.name || '').trim()));

  const filteredExams = exams
    .filter(exam => {
      if (examSearch === '') return true;
      const searchLower = examSearch.toLowerCase();
      const examNameLower = exam.name.toLowerCase();
      const cleanedExamNameLower = cleanExamName(exam.name, exam.examLevel).toLowerCase();
      return examNameLower.includes(searchLower) || cleanedExamNameLower.includes(searchLower);
    })
    .reduce((unique, exam) => {
      const exists = unique.some(e => e.name === exam.name);
      if (!exists) {
        unique.push(exam);
      }
      return unique;
    }, [])
    .sort(sortExamsByAcronym);

  const filteredExamTypes = examTypes
    .filter(type => type.toLowerCase().includes(examTypeSearch.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));

  const isMobileView = useWindowSize();

    const loadData = async () => {
      try {
        setIsSearchLoading(true);
        console.log('Loading data with strict 5-minute cache policy...');
        
        // Only attempt to get data from the 5-minute cache, no fallbacks
        const examData = await fetchExamData(props.pluginUrl, bypassCache);
        
        if (!examData) {
          console.error('No data received from cache - cache may be expired or invalid');
          setIsSearchLoading(false);
          return;
        }

        let parsedData;
        
        // Check if we got JSON data directly (from cached API) or XML data
        if (typeof examData === 'object' && examData.providers) {
          console.log('Processing JSON data from cached API');
          parsedData = processJsonData(examData);
        } else if (typeof examData === 'string') {
          console.log('Processing XML data from cache');
          parsedData = parseExamProviderData(examData);
        } else {
          console.error('Unknown data format received:', typeof examData);
          setIsSearchLoading(false);
          return;
        }
        
        // Only update state if parsing was successful and data is valid
        if (parsedData && parsedData.providers && parsedData.providers.length > 0) {
          const sortedProviders = parsedData.providers.sort((a, b) => (a.name || '').trim().localeCompare((b.name || '').trim()));
          console.log('Setting exam providers from fresh cache, count:', sortedProviders.length);
          
          setExamProviders(sortedProviders);
          setCountries(parsedData.countries);
          setLanguages(parsedData.languages);
          setExams(parsedData.exams);
          
          // Extract unique exam types from all providers
          const uniqueExamTypes = [...new Set(
            sortedProviders.flatMap(provider => 
              Object.values(provider.exams || {}).flat().flatMap(exam => 
                exam.typeOfExams || []
              )
            )
          )].filter(type => type && type.trim() !== '').sort();
          
          setExamTypes(uniqueExamTypes);
          setCacheExpiryTime(Date.now() + (5 * 60 * 1000)); // 5 minutes from now
          
          console.log('Successfully loaded fresh data from cache');
        } else {
          console.error('Parsed data was invalid or empty - no fallback data will be used');
          // Clear all data if invalid
          setExamProviders([]);
          setCountries([]);
          setLanguages([]);
          setExams([]);
          setExamTypes([]);
        }
        
        setIsSearchLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        console.log('No fallback data - system will show empty state');
        
        // Clear all data on error - no fallbacks allowed
        setExamProviders([]);
        setCountries([]);
        setLanguages([]);
        setExams([]);
        setExamTypes([]);
        setIsSearchLoading(false);
      }
    };

  useEffect(() => {
    loadData();
  }, [useApi, bypassCache]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setCountryOpen(false)
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageOpen(false)
      }
      if (examProviderRef.current && !examProviderRef.current.contains(event.target)) {
        setExamProviderOpen(false)
      }
      if (examRef.current && !examRef.current.contains(event.target)) {
        setExamOpen(false)
      }
      if (examTypeRef.current && !examTypeRef.current.contains(event.target)) {
        setExamTypeOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const clearAllSearches = () => {
    setCountrySearch("")
    setLanguageSearch("")
    setExamProviderSearch("")
    setExamSearch("")
    setExamTypeSearch("")
    setCountryOpen(false)
    setLanguageOpen(false)
    setExamProviderOpen(false)
    setExamOpen(false)
    setExamTypeOpen(false)
  }

  // Add shuffle function
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const handleSearch = () => {
    setIsSearchLoading(true);
    
    const results = examProviders.filter(provider => {
      const matchesProvider = !examProviderSearch || 
        (provider.name || '').toLowerCase().includes(examProviderSearch.toLowerCase());
      
      const matchesCountry = !countrySearch || 
        (provider.countriesOfOperation && (provider.countriesOfOperation || '').split(',').some(c => 
          (c || '').toLowerCase().trim().includes(countrySearch.toLowerCase())
        )) || (provider.country || '').toLowerCase().includes(countrySearch.toLowerCase());
      
      // Enhanced exam and language filtering
      // If both exam and language are specified, check that the exam exists in that language
      if (examSearch && languageSearch) {
        const matchesExamAndLanguage = Object.values(provider.exams || {}).flat().some(exam => {
          const searchLower = examSearch.toLowerCase();
          const examNameLower = (exam.name || '').toLowerCase();
          const cleanedExamNameLower = cleanExamName(exam.name, exam.examLevel).toLowerCase();
          const examMatches = examNameLower === searchLower || cleanedExamNameLower === searchLower;
          const hasLanguage = Array.isArray(exam.languages) && exam.languages.some(lang => 
            (lang || '').toLowerCase().includes(languageSearch.toLowerCase())
          );
          return examMatches && hasLanguage;
        });
        var matchesExam = matchesExamAndLanguage;
        var matchesLanguage = matchesExamAndLanguage;
      } else {
        // If only one is specified, use the original logic
        var matchesExam = !examSearch || 
          Object.values(provider.exams || {}).flat().some(exam => {
            const searchLower = examSearch.toLowerCase();
            const examNameLower = (exam.name || '').toLowerCase();
            const cleanedExamNameLower = cleanExamName(exam.name, exam.examLevel).toLowerCase();
            return examNameLower === searchLower || cleanedExamNameLower === searchLower;
          });
        
        var matchesLanguage = !languageSearch || 
          Object.values(provider.exams || {}).flat().some(exam => 
            Array.isArray(exam.languages) && exam.languages.some(lang => 
              (lang || '').toLowerCase().includes(languageSearch.toLowerCase())
            )
          );
      }
      
      const matchesExamType = !examTypeSearch || (() => {
        // Debug: log exam types for the first provider
        if (provider === examProviders[0]) {
          console.log('Debug exam types for first provider:', provider.name);
          Object.entries(provider.exams || {}).forEach(([type, exams]) => {
            console.log(`  Type key: "${type}", Exams:`, exams.map(e => ({name: e.name, type: e.type, typeOfExams: e.typeOfExams})));
          });
        }
        
        const searchType = examTypeSearch.toLowerCase();
        
        // Check if any exam in the provider has the search type in its typeOfExams array
        return Object.values(provider.exams || {}).flat().some(exam => {
          const typeOfExams = exam.typeOfExams || [];
          return typeOfExams.some(examType => 
            examType.toLowerCase().includes(searchType)
          );
        });
      })();
      
      return matchesProvider && matchesCountry && matchesExam && matchesLanguage && matchesExamType;
    });
    
    // Only sort by preferred status and multiple boards if country filter is active
    if (countrySearch) {
      console.log(`Checking preferred providers for country: ${countrySearch}`);
      
      // Check if provider's country matches the search country
      const isPreferredValid = (provider) => {
        if (!provider.preferred) {
          console.log(`Provider ${provider.name} is not preferred, skipping`);
          return false;
        }
        
        // Get the search country in lowercase for comparison
        const searchCountryLower = countrySearch.toLowerCase().trim();
        
        console.log(`Checking provider: ${provider.name}`, {
          preferred: provider.preferred,
          boards: provider.boards,
          rawBoards: provider.ExamProviderBoards
        });
        
        // Get all countries of operation from the provider's boards
        const boardCountriesOfOperation = provider.boards?.map(board => {
          console.log('Processing board:', board);
          // Each board's countries of operation is a comma-separated string
          const countries = (board.countriesOfOperation || '')
            .split(',')
            .map(c => c.toLowerCase().trim())
            .filter(c => c); // Remove empty strings
          console.log('Board countries:', countries);
          return countries;
        }).flat() || [];

        console.log('All board countries:', boardCountriesOfOperation);

        const hasMatch = boardCountriesOfOperation.includes(searchCountryLower);
        
        if (hasMatch) {
          console.log(`Found match for ${searchCountryLower}:`, {
            providerName: provider.name,
            preferred: provider.preferred,
            boardCountries: boardCountriesOfOperation
          });
        } else {
          console.log(`No match found for ${searchCountryLower} in provider ${provider.name}`, {
            searchCountry: searchCountryLower,
            availableCountries: boardCountriesOfOperation
          });
        }
        
        return hasMatch;
      };

      // Check if provider has the search country in their boards (for secondary sort)
      const hasCountryInBoards = (provider) => {
        // Get the search country in lowercase for comparison
        const searchCountryLower = countrySearch.toLowerCase().trim();
        
        // Get all countries of operation from the provider's boards
        const boardCountriesOfOperation = provider.boards?.map(board => {
          return (board.countriesOfOperation || '')
            .split(',')
            .map(c => c.toLowerCase().trim())
            .filter(c => c); // Remove empty strings
        }).flat() || [];

        return boardCountriesOfOperation.includes(searchCountryLower);
      };

      // Check if provider has the search country in their main countries of operation (for tertiary sort)
      const hasCountryInMainOperations = (provider) => {
        // Get the search country in lowercase for comparison
        const searchCountryLower = countrySearch.toLowerCase().trim();
        
        // Get the provider's main countries of operation
        const mainCountriesOfOperation = (provider.countriesOfOperation || '')
          .split(',')
          .map(c => c.toLowerCase().trim())
          .filter(c => c);

        return mainCountriesOfOperation.includes(searchCountryLower);
      };

      // Split into four groups: valid preferred, with country in boards, with country in main operations, and others
      const preferredProviders = results.filter(provider => isPreferredValid(provider));
      console.log(`Found ${preferredProviders.length} preferred providers matching country ${countrySearch}`);
      
      const countryInBoardsProviders = results.filter(provider => !isPreferredValid(provider) && hasCountryInBoards(provider));
      const countryInMainProviders = results.filter(provider => !isPreferredValid(provider) && !hasCountryInBoards(provider) && hasCountryInMainOperations(provider));
      
      // Shuffle each group separately
      const shuffledPreferred = shuffleArray([...preferredProviders]);
      const shuffledCountryInBoards = shuffleArray([...countryInBoardsProviders]);
      const shuffledCountryInMain = shuffleArray([...countryInMainProviders]);
      
      // Combine only the three relevant groups in priority order
      setSearchResults([...shuffledPreferred, ...shuffledCountryInBoards, ...shuffledCountryInMain]);
    } else {
      // If no country filter, just shuffle all results
      setSearchResults(shuffleArray([...results]));
    }
    
    setIsSearchLoading(false);
  };

  // Fix the MobileResultCard component
  const MobileResultCard = ({ provider, setSelectedProvider, setShowModal }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
        <h3 className="text-lg font-medium text-gray-900">{provider.name}</h3>
        </div>
        <a
          href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-custom-red hover:bg-custom-red-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-red"
        >
          Website
        </a>
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-500">Email</h4>
          <p className="mt-1 text-sm text-gray-900">
            {provider.email ? (
              <a href={`mailto:${provider.email}`} className="text-blue-600 hover:text-blue-800 underline">
                {provider.email}
              </a>
            ) : 'N/A'}
          </p>
        </div>
      </div>
      
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => {
            setSelectedProvider(provider);
            setShowModal(true);
          }}
          className="text-gray-600 hover:text-gray-900 flex items-center"
        >
          <span className="mr-1">More Info</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  function clearExamProvidersCache() {
    return fetch('/wp-json/istqb/v1/clear-cache', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        alert('Cache cleared successfully!');
        // Reload data after clearing cache if not bypassing cache
        if (!bypassCache) {
          loadData();
        }
      } else {
        throw new Error(data.message || 'Failed to clear cache');
      }
    })
    .catch(error => {
      console.error('Error clearing cache:', error);
      alert('Failed to clear cache: ' + error.message);
    });
  }

  function checkCacheStatus() {
    return fetch('/wp-json/istqb/v1/cache-status', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => response.json())
    .then(data => {
      const status = [
        `Cache exists: ${data.cache_file_exists ? 'Yes' : 'No'}`,
        `Cache duration: ${data.cache_duration_minutes} minutes`,
        data.cache_file_exists ? `Cache age: ${data.cache_file_age_seconds} seconds` : '',
        data.cache_file_exists ? `Expired: ${data.cache_is_expired ? 'Yes' : 'No'}` : '',
        `Next refresh: ${data.next_cron_refresh}`,
        data.time_until_next_refresh ? `Time until refresh: ${data.time_until_next_refresh} seconds` : ''
      ].filter(Boolean).join('\n');
      
      alert('Cache Status:\n\n' + status);
    })
    .catch(error => {
      console.error('Error checking cache status:', error);
      alert('Failed to check cache status: ' + error.message);
    });
  }

  function manualRefreshCache() {
    return fetch('/wp-json/istqb/v1/refresh-cache', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        alert('Cache refreshed successfully at: ' + data.timestamp);
        // Reload data after refreshing cache if not bypassing cache
        if (!bypassCache) {
          loadData();
        }
      } else {
        throw new Error(data.message || 'Failed to refresh cache');
      }
    })
    .catch(error => {
      console.error('Error refreshing cache:', error);
      alert('Failed to refresh cache: ' + error.message);
    });
  }

  const renderDevTools = () => {
    if (window.location.href.includes('devlocal.local')) {
      return (
        <div className="flex items-center gap-2 mb-4">
          {isDevelopment() && (
            <div className="flex items-center">
              <span className="mr-3 text-sm text-gray-600">
                {useApi ? 'Using API' : 'Using Local XML'}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={useApi}
                  onChange={() => setUseApi(!useApi)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          )}
          <button
            onClick={clearExamProvidersCache}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium text-gray-700"
          >
            Clear Cache
          </button>
          <button
            onClick={checkCacheStatus}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium text-gray-700"
          >
            Check Cache Status
          </button>
          <button
            onClick={manualRefreshCache}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium text-gray-700"
          >
            Manual Refresh Cache
          </button>
        </div>
      );
    }
    return null;
  };

  // Add this function to determine sort status
  const getSortStatus = (provider) => {
    if (!countrySearch) return null;
    
    const searchCountryLower = countrySearch.toLowerCase().trim();
    
    // Check if preferred AND has matching country in boards
    if (provider.preferred && provider.boards?.some(board => 
      (board.countriesOfOperation || '').split(',').map(c => c.toLowerCase().trim()).includes(searchCountryLower)
    )) {
      return 'primary';
    }
    
    // Check if has matching country in boards (but not preferred)
    if (provider.boards?.some(board => 
      (board.countriesOfOperation || '').split(',').map(c => c.toLowerCase().trim()).includes(searchCountryLower)
    )) {
      return 'secondary';
    }
    
    // Check if has matching country in main countries of operation
    if ((provider.countriesOfOperation || '').split(',').map(c => c.toLowerCase().trim()).includes(searchCountryLower)) {
      return 'tertiary';
    }
    
    return null;
  };

     // Tooltip component for exam type information
   const ExamTypeTooltip = () => {
     const [showTooltip, setShowTooltip] = useState(false);
     
     const tooltipContent = (
       <div className="text-sm">
         <div className="mb-2"><strong>Paper:</strong> Traditional paper-based exam at a test venue under the supervision of an in-person proctor</div>
         <div className="mb-2"><strong>Online:</strong> Exam on online platform at a test venue under the supervision of an in-person proctor</div>
         <div className="mb-2"><strong>Remote:</strong> Exam on online platform from your home or office under the supervision of a remote proctor</div>
         <div className="mt-4 text-gray-600 text-xs italic">Note: Both paper-based exams and online exams can be administered either at a dedicated test venue or at your organization's own office location, provided an in-person proctor is present.</div>
       </div>
     );

     return (
       <div className="relative inline-block" style={{ display: 'inline-block', position: 'relative' }}>
         <button
           className="text-gray-400 hover:text-gray-600 focus:outline-none p-1"
           style={{
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             padding: '4px',
             margin: '0',
             border: 'none',
             background: 'transparent',
             outline: 'none',
             verticalAlign: 'middle',
             lineHeight: '1'
           }}
           onMouseEnter={() => setShowTooltip(true)}
           onMouseLeave={() => setShowTooltip(false)}
           onClick={(e) => {
             e.preventDefault();
             e.stopPropagation();
             setShowTooltip(!showTooltip);
           }}
           title="Exam type information"
         >
           <svg 
             className="w-4 h-4" 
             fill="none" 
             stroke="currentColor" 
             viewBox="0 0 24 24"
           >
             <path
               strokeLinecap="round"
               strokeLinejoin="round"
               strokeWidth={2}
               d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
             />
           </svg>
         </button>
         
         {showTooltip && (
           <div className="absolute z-50 w-80 p-3 mt-2 text-sm bg-white border border-gray-200 rounded-lg shadow-lg -left-80 lg:-left-72">
             <div className="absolute -top-1 left-72 lg:left-1/2 transform lg:-translate-x-1/2">
               <div className="w-2 h-2 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
             </div>
             {tooltipContent}
           </div>
         )}
       </div>
     );
  };

  // Add a component to show no data state
  const NoDataMessage = () => {
    if (isSearchLoading) return null;
    
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <div className="max-w-md mx-auto">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No exam provider data available</h3>
          <p className="mt-1 text-sm text-gray-500">
            System uses strict 5-minute cache policy. Data may be temporarily unavailable while cache refreshes.
          </p>
          <div className="mt-6">
            <button
              onClick={() => loadData()}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Try Again
            </button>
          </div>
          {cacheExpiryTime && (
            <p className="mt-2 text-xs text-gray-400">
              Next cache refresh in: {Math.max(0, Math.ceil((cacheExpiryTime - Date.now()) / 1000))} seconds
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="my-unique-plugin-wrapper-class" id={`istqb-exam-provider-wrapper-${props.containerId || 'main'}`} style={{ width: '100vw', maxWidth: '100%', margin: '0', padding: '0 20px' }}>
      <div className="flex items-center justify-between mb-6">
        <h1 
          className={`text-3xl font-bold text-gray-900 ${!isProductionDomain() ? 'cursor-pointer hover:text-blue-600' : ''} transition-colors duration-200`}
          {...(!isProductionDomain() && {
            onClick: () => setShowCacheControls(!showCacheControls),
            title: "Click to toggle cache controls"
          })}
        >
          Exam Provider
        </h1>
      </div>
      
      {/* Cache Controls Panel - only show if not production domain */}
      {showCacheControls && !isProductionDomain() && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">Cache Controls</h3>
          <div className="flex flex-wrap items-center gap-3">
            {/* Bypass Cache Toggle */}
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <span className="mr-3 text-sm font-medium text-blue-700">
                  Bypass Cache: {bypassCache ? 'ON' : 'OFF'}
                </span>
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={bypassCache}
                    onChange={(e) => setBypassCache(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </div>
              </label>
            </div>
            
            {/* Cache Action Buttons */}
            <button
              onClick={checkCacheStatus}
              className="px-3 py-1 bg-blue-200 hover:bg-blue-300 rounded-md text-sm font-medium text-blue-800 transition-colors"
            >
              Check Status
            </button>
            <button
              onClick={manualRefreshCache}
              className="px-3 py-1 bg-green-200 hover:bg-green-300 rounded-md text-sm font-medium text-green-800 transition-colors"
            >
              Refresh Cache
            </button>
            <button
              onClick={clearExamProvidersCache}
              className="px-3 py-1 bg-red-200 hover:bg-red-300 rounded-md text-sm font-medium text-red-800 transition-colors"
            >
              Clear Cache
            </button>
            
            {/* Status Indicator */}
            <div className="flex items-center text-sm">
              <div className={`w-2 h-2 rounded-full mr-2 ${bypassCache ? 'bg-orange-500' : 'bg-green-500'}`}></div>
              <span className="text-gray-600">
                {bypassCache ? 'Direct API' : 'Using Cache'}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {renderDevTools()}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg">ISTQB® exams are offered through external exam providers and member boards acting as exam providers. An exam provider is an organization licensed by a member board(s) to offer exams locally and internationally.

To display the list of all available exam providers, click "Search".</h2>
      </div>
      <br />
      
      {/* Desktop Dropdowns */}
      {!isMobileView && (
        <div id="desktop-dropdowns" className="p-6 rounded-lg mb-6">
          {/* Filters row */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Country Search */}
            <div className="relative" ref={countryRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for country..."
                  value={countrySearch}
                  onChange={(e) => {
                    setCountrySearch(e.target.value)
                    setCountryOpen(true)
                  }}
                  onFocus={() => setCountryOpen(true)}
                />
                <ClearButton 
                  show={countrySearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setCountrySearch("")
                    setCountryOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setCountryOpen(!countryOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${countryOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {countryOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {countries.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredCountries.map((country, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setCountrySearch(country)
                        setCountryOpen(false)
                      }}
                    >
                      {country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Language Search */}
            <div className="relative" ref={languageRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for language..."
                  value={languageSearch}
                  onChange={(e) => {
                    setLanguageSearch(e.target.value)
                    setLanguageOpen(true)
                  }}
                  onFocus={() => setLanguageOpen(true)}
                />
                <ClearButton 
                  show={languageSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setLanguageSearch("")
                    setLanguageOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setLanguageOpen(!languageOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${languageOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {languageOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {languages.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredLanguages.map((language, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setLanguageSearch(language)
                        setLanguageOpen(false)
                      }}
                    >
                      {language}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Exam Provider and Exam row */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Exam Provider Search */}
            <div className="relative" ref={examProviderRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for exam provider..."
                  value={examProviderSearch}
                  onChange={(e) => {
                    setExamProviderSearch(e.target.value)
                    setExamProviderOpen(true)
                  }}
                  onFocus={() => setExamProviderOpen(true)}
                />
                <ClearButton 
                  show={examProviderSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setExamProviderSearch("")
                    setExamProviderOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setExamProviderOpen(!examProviderOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${examProviderOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {examProviderOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {examProviders.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredExamProviders.map((provider, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setExamProviderSearch(provider.name)
                        setExamProviderOpen(false)
                      }}
                    >
                      {provider.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Exam and Exam Type combined column */}
            <div className="flex gap-2">
              {/* Exam Search - takes 3/5 of the space */}
              <div className="relative w-3/5" ref={examRef}>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search for exam..."
                    value={examSearch}
                    onChange={(e) => {
                      setExamSearch(e.target.value)
                      setExamOpen(true)
                    }}
                    onFocus={() => setExamOpen(true)}
                  />
                  <ClearButton 
                    show={examSearch !== ""} 
                    onClick={(e) => {
                      e.stopPropagation()
                      setExamSearch("")
                      setExamOpen(false)
                    }} 
                  />
                  <button 
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                    onClick={() => setExamOpen(!examOpen)}
                    title="Toggle dropdown"
                  >
                    <svg 
                      className={`w-5 h-5 transition-transform duration-200 ${examOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
                
                {examOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {exams.length === 0 ? (
                      <DropdownLoader />
                    ) : (() => {
                      const groupedExams = groupExamsForDropdown(filteredExams);
                      return Object.entries(groupedExams).map(([type, typeLevels]) => (
                        <div key={type}>
                          <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm border-b border-gray-200">
                            {type}
                          </div>
                          {Object.entries(typeLevels).map(([level, levelExams]) => (
                            <div key={`${type}-${level}`}>
                              <div className="px-6 py-1 bg-gray-25 font-medium text-gray-600 text-xs border-b border-gray-100" style={{backgroundColor: '#f8f9fa'}}>
                                {level}
                              </div>
                              {levelExams.map((exam, index) => (
                                <div
                                  key={`${type}-${level}-${index}`}
                                  className="px-8 py-2 cursor-pointer hover:bg-gray-100"
                                  onClick={() => {
                                    setExamSearch(cleanExamName(exam.name, exam.examLevel));
                                    setExamOpen(false);
                                  }}
                                >
                                  {cleanExamName(exam.name, exam.examLevel)}
                                  <span className="text-gray-500 text-sm ml-2">
                                    ({exam.type}{exam.shortAcronym ? ` - ${exam.shortAcronym}` : ''})
                                  </span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>

              {/* Exam Type Search - takes 2/5 of the space */}
              <div className="w-2/5 flex items-start gap-2" ref={examTypeRef}>
                <div className="relative flex-1">
                  <input
                    type="text"
                    className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Exam type..."
                    value={examTypeSearch}
                    onChange={(e) => {
                      setExamTypeSearch(e.target.value)
                      setExamTypeOpen(true)
                    }}
                    onFocus={() => setExamTypeOpen(true)}
                  />
                  <ClearButton 
                    show={examTypeSearch !== ""} 
                    onClick={(e) => {
                      e.stopPropagation()
                      setExamTypeSearch("")
                      setExamTypeOpen(false)
                    }} 
                  />
                  <button 
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                    onClick={() => setExamTypeOpen(!examTypeOpen)}
                    title="Toggle dropdown"
                  >
                    <svg 
                      className={`w-5 h-5 transition-transform duration-200 ${examTypeOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                
                {examTypeOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {examTypes.length === 0 ? (
                      <DropdownLoader />
                      ) : filteredExamTypes.map((type, index) => {
                        const displayType = type.toLowerCase().includes('remote proctoring') 
                          ? type.replace(/remote proctoring/gi, 'Remote') 
                          : type;
                        return (
                      <div
                        key={index}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                              setExamTypeSearch(displayType);
                          setExamTypeOpen(false);
                        }}
                      >
                            {displayType}
                      </div>
                        );
                      })}
                  </div>
                )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '0', marginBottom: '0', padding: '0' }}>
                  <ExamTypeTooltip />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Clear buttons row */}
          <div className="w-full flex items-center">
            <button
              onClick={handleSearch}
              style={{ 
                backgroundColor: '#c43e4b',
                color: 'white',
                padding: '0.5rem 1.5rem',
                borderRadius: '0.5rem',
                transition: 'background-color 200ms'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#a8363f'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#c43e4b'}
            >
              Search
            </button>

            <button
              onClick={clearAllSearches}
              className="ml-auto text-gray-600 hover:text-gray-800 px-6 py-2"
              style={{ float: 'right' }}
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Mobile Dropdowns */}
      {isMobileView && (
        <div id="mobile-dropdowns" className="p-6 rounded-lg mb-6">
          <div className="flex flex-col space-y-4">
            {/* Country Search */}
            <div className="relative" ref={countryRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for country..."
                  value={countrySearch}
                  onChange={(e) => {
                    setCountrySearch(e.target.value)
                    setCountryOpen(true)
                  }}
                  onFocus={() => setCountryOpen(true)}
                />
                <ClearButton 
                  show={countrySearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setCountrySearch("")
                    setCountryOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setCountryOpen(!countryOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${countryOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {countryOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {countries.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredCountries.map((country, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setCountrySearch(country)
                        setCountryOpen(false)
                      }}
                    >
                      {country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Language Search */}
            <div className="relative" ref={languageRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for language..."
                  value={languageSearch}
                  onChange={(e) => {
                    setLanguageSearch(e.target.value)
                    setLanguageOpen(true)
                  }}
                  onFocus={() => setLanguageOpen(true)}
                />
                <ClearButton 
                  show={languageSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setLanguageSearch("")
                    setLanguageOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setLanguageOpen(!languageOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${languageOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {languageOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {languages.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredLanguages.map((language, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setLanguageSearch(language)
                        setLanguageOpen(false)
                      }}
                    >
                      {language}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Exam Provider Search */}
            <div className="relative" ref={examProviderRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for exam provider..."
                  value={examProviderSearch}
                  onChange={(e) => {
                    setExamProviderSearch(e.target.value)
                    setExamProviderOpen(true)
                  }}
                  onFocus={() => setExamProviderOpen(true)}
                />
                <ClearButton 
                  show={examProviderSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setExamProviderSearch("")
                    setExamProviderOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setExamProviderOpen(!examProviderOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${examProviderOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {examProviderOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {examProviders.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredExamProviders.map((provider, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setExamProviderSearch(provider.name)
                        setExamProviderOpen(false)
                      }}
                    >
                      {provider.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Exam Search */}
            <div className="relative" ref={examRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for exam..."
                  value={examSearch}
                  onChange={(e) => {
                    setExamSearch(e.target.value)
                    setExamOpen(true)
                  }}
                  onFocus={() => setExamOpen(true)}
                />
                <ClearButton 
                  show={examSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setExamSearch("")
                    setExamOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setExamOpen(!examOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${examOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {examOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {exams.length === 0 ? (
                    <DropdownLoader />
                  ) : (() => {
                    const groupedExams = groupExamsForDropdown(filteredExams);
                    return Object.entries(groupedExams).map(([type, typeLevels]) => (
                      <div key={type}>
                        <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm border-b border-gray-200">
                          {type}
                        </div>
                        {Object.entries(typeLevels).map(([level, levelExams]) => (
                          <div key={`${type}-${level}`}>
                            <div className="px-6 py-1 bg-gray-25 font-medium text-gray-600 text-xs border-b border-gray-100" style={{backgroundColor: '#f8f9fa'}}>
                              {level}
                            </div>
                            {levelExams.map((exam, index) => (
                              <div
                                key={`${type}-${level}-${index}`}
                                className="px-8 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  setExamSearch(exam.name);
                                  setExamOpen(false);
                                }}
                              >
                                {exam.name}
                                <span className="text-gray-500 text-sm ml-2">
                                  ({exam.type}{exam.shortAcronym ? ` - ${exam.shortAcronym}` : ''})
                                </span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Exam Type Search */}
            <div className="flex items-start gap-2" ref={examTypeRef}>
              <div className="relative flex-1">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for exam type..."
                  value={examTypeSearch}
                  onChange={(e) => {
                    setExamTypeSearch(e.target.value)
                    setExamTypeOpen(true)
                  }}
                  onFocus={() => setExamTypeOpen(true)}
                />
                <ClearButton 
                  show={examTypeSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setExamTypeSearch("")
                    setExamTypeOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setExamTypeOpen(!examTypeOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${examTypeOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                
              {examTypeOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {examTypes.length === 0 ? (
                    <DropdownLoader />
                    ) : filteredExamTypes.map((type, index) => {
                      const displayType = type.toLowerCase().includes('remote proctoring') 
                        ? type.replace(/remote proctoring/gi, 'Remote') 
                        : type;
                      return (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                            setExamTypeSearch(displayType);
                        setExamTypeOpen(false);
                      }}
                    >
                          {displayType}
                    </div>
                      );
                    })}
                </div>
              )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '0', marginBottom: '0', padding: '0' }}>
                <ExamTypeTooltip />
              </div>
            </div>

            {/* Search and Clear buttons row - same as desktop */}
            <div className="w-full flex items-center">
              <button
                onClick={handleSearch}
                style={{ 
                  backgroundColor: '#c43e4b',
                  color: 'white',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '0.5rem',
                  transition: 'background-color 200ms'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#a8363f'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#c43e4b'}
              >
                Search
              </button>

              <button
                onClick={clearAllSearches}
                className="ml-auto text-gray-600 hover:text-gray-800 px-6 py-2"
                style={{ float: 'right' }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Results or No Data Message */}
      {isSearchLoading ? (
        <Loader />
      ) : examProviders.length === 0 ? (
        <NoDataMessage />
      ) : searchResults.length > 0 ? (
        <>
          {/* Desktop Table View */}
          {!isMobileView && searchResults.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <div className="grid grid-cols-4 mb-2">
                <div className="col-span-3"></div>
                <div className="text-center text-sm text-gray-600">
                  {searchResults.length} results found
                </div>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => setShowSortLabels(!showSortLabels)}
                      title="Click to toggle sort labels"
                    >
                      Exam Provider
                    </th>
                    <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="w-1/4 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      More Information
                    </th>
                    <th className="w-1/4 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Website
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {searchResults.map((provider, index) => {
                    const sortStatus = getSortStatus(provider);
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div 
                              className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                              onClick={() => {
                                setSelectedProvider(provider)
                                setShowModal(true)
                              }}
                              title={provider.name}
                            >
                              {truncateText(provider.name)}
                            </div>
                            {showSortLabels && sortStatus && (
                              <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                sortStatus === 'primary' 
                                  ? 'bg-green-100 text-green-800' 
                                  : sortStatus === 'secondary'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {sortStatus}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {provider.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => {
                              setSelectedProvider(provider)
                              setShowModal(true)
                            }}
                            className="text-gray-600 hover:text-gray-900"
                            title="More Information"
                          >
                            <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <a
                            href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-custom-red hover:bg-custom-red-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-red"
                          >
                            Go
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile Card View */}
          {isMobileView && searchResults.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-end mb-2">
                <div className="text-sm text-gray-600 pr-4">
                  {searchResults.length} results found
                </div>
              </div>
              {searchResults.map((provider, index) => (
                <MobileResultCard 
                  key={index} 
                  provider={provider} 
                  setSelectedProvider={setSelectedProvider} 
                  setShowModal={setShowModal} 
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {/* Modal */}
      {showModal && selectedProvider && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 h-full w-full z-50 p-4">
          <div className="relative top-20 mx-auto border w-full max-w-2xl shadow-lg rounded-md bg-white">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white p-4 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold" style={{ color: '#215184' }}>
                  {selectedProvider.name}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 max-h-[calc(100vh-240px)] overflow-y-auto">
              <div className="text-sm">
                <h4 className="font-semibold mb-2 text-gray-700">Details</h4>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold mb-2 text-gray-700">Exam Provider Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-gray-600">
                    <p><span className="font-medium">Name:</span> {selectedProvider.name}</p>
                    <p><span className="font-medium">Reference Person:</span> {selectedProvider.referencePerson}</p>
                    <p className="col-span-2"><span className="font-medium">Address:</span> {selectedProvider.address}</p>
                    <p><span className="font-medium">City:</span> {selectedProvider.city}</p>
                    <p><span className="font-medium">Headquarters:</span> {selectedProvider.country}</p>
                    <p><span className="font-medium">Phone:</span> {selectedProvider.phone}</p>
                    <p><span className="font-medium">E-Mail:</span> <a href={`mailto:${selectedProvider.email}`} className="text-blue-600 hover:text-blue-800 underline">{selectedProvider.email}</a></p>
                    <p><span className="font-medium">Web Site:</span> <a href={selectedProvider.website.startsWith('http') ? selectedProvider.website : `https://${selectedProvider.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">{selectedProvider.website}</a></p>
                  </div>

                  {/* Exams Section */}
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2 text-gray-700">Available Exams</h4>
                    
                    {(() => {
                      // Ensure exam types are displayed in correct order: Core, Specialist, Expert
                      const typeOrder = ['Core', 'Specialist', 'Expert'];
                      const orderedTypes = typeOrder.filter(type => 
                        selectedProvider.exams[type] && selectedProvider.exams[type].length > 0
                      );
                      
                      return orderedTypes.map(type => {
                        const exams = selectedProvider.exams[type];
                        const sortedExams = exams.sort(sortExamsByAcronym);
                        const levelGroups = groupExamsByLevel(sortedExams);
                        
                        return (
                        <div key={type} className="mb-4 border-l-4 border-blue-500 pl-4">
                          <h5 className="font-bold text-lg text-blue-600 mb-3">{type}</h5>
                          
                          {Object.entries(levelGroups).map(([level, levelExams]) => (
                            <div key={`${type}-${level}`} className="mb-3">
                              <h6 className="font-semibold text-md text-gray-700 mb-2 ml-2">
                                {level}
                              </h6>
                              <ul className="space-y-2">
                                {levelExams.map((exam, index) => (
                                  <li key={`${level}-${index}`} className="border-b border-gray-100 pb-2">
                                    <p className="font-medium">• {cleanExamName(exam.name, exam.examLevel)}</p>
                                    <div className="text-sm text-gray-600 ml-4">
                                      {exam.shortAcronym && <p>Acronym: {exam.shortAcronym}</p>}
                                      {exam.examLevel && <p>Level: {exam.examLevel}</p>}
                                      {exam.languages.length > 0 && (
                                        <p>Languages: {exam.languages.join(', ')}</p>
                                      )}
                                      {exam.typeOfExams.length > 0 && (
                                        <p>Exam Types: {exam.typeOfExams.join(', ')}</p>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
